import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller } from 'react-hook-form';
import MenuItem from '@material-ui/core/MenuItem';
import PhysicianUtils from 'services/PhysicianUtils';
import StaffUtils from 'services/StaffUtils';
import StripeUtils from 'services/StripeUtils';
import { TopBar } from 'components/Patient/CPP/CPPInput/Inputs/CPPInputCommonViews';
import moment from 'moment-timezone';
import './style.scss';
import ValidatorUtils from 'services/ValidatorUtils';
import CalendarUtils from 'services/CalendarUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import { useReduxForm } from 'hooks/useReduxForm';

const DATE_FORMAT = 'YYYY/MM/DD';

const administeredDrugSchema = z.object({
  name: z.string().optional().or(z.literal('')),
  description: z.string().optional().or(z.literal('')),
  route: z.string().optional().or(z.literal('')),
  site: z.string().optional().or(z.literal('')),
  lot: z.string().optional().or(z.literal('')),
  expiry_date: z.string().optional().or(z.literal('')),
  given_date: z.string().optional().or(z.literal('')),
  scheduled_date: z.string().optional(),
  dosage: z.string().optional().or(z.literal('')),
  given_by: z.string().optional().or(z.literal('')),
  notes: z.string().optional().or(z.literal('')),
  followUp: z.boolean().optional(),
  followUpDate: z.any(),
  feeEntryId: z.string().optional().or(z.literal('')),
  feeId: z.string().optional().or(z.literal('')),
  paid: z.boolean().optional(),
  itemId: z.any(),
  patient_id: z.any(),
});

type AdministeredDrugValues = z.infer<typeof administeredDrugSchema>;

const NewAdministeredDrug = ({
  patient,
  onClose,
  form,
}: {
  patient: any;
  onClose?: () => void;
  form: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [physicianNames, setPhysicianNames] = useState<string[]>([]);
  const [vitalitySelection, setVitalitySelection] = useState('');
  const inFlightRef = useRef(false);

  const dispatch = useAppDispatch();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const drugDescriptions = useAppSelector((state) => state.administeredDrugDescriptions);
  const authentication = useAppSelector((state) => state.authentication);

  const { control, handleSubmit, setValue, watch, reset, getValues } =
    useReduxForm<AdministeredDrugValues>(form, {
      destroyOnUnmount: false,
      resolver: zodResolver(administeredDrugSchema),
      defaultValues: {
        name: '',
        description: '',
        route: '',
        site: '',
        lot: '',
        expiry_date: undefined,
        given_date: undefined,
        scheduled_date: undefined,
        dosage: '',
        given_by: '',
        notes: '',
        followUp: false,
        followUpDate: undefined,
        feeEntryId: '',
        feeId: '',
        paid: false,
        itemId: undefined,
        patient_id: undefined,
      },
    });

  const itemId = watch('itemId');
  const given_date = watch('given_date');
  const scheduled_date = watch('scheduled_date');
  const expiry_date = watch('expiry_date');
  const nameValue = watch('name');
  const followUp = watch('followUp');
  const feeEntryId = watch('feeEntryId');
  const feeId = watch('feeId');
  const paid = watch('paid');
  const given_by = watch('given_by');

  // Re-runs whenever Given By falls empty: the mount-only version left it blank
  // for every record entered after the first, because reset() clears the field
  // and nothing refilled it. Also covers the physician list landing after mount.
  useEffect(() => {
    getPhysicianNames();
  }, [physicians, given_by, itemId, authentication.user?.id]);

  // Fees are tenant fee-schedule data — refetch on every form open so an
  // admin's midday price change can't ride a stale login-time preload.
  useEffect(() => {
    dispatch(api.HealthRecords.getVitalityDrugDescriptions());
  }, []);

  // Auto-convert unix timestamps to date strings
  useEffect(() => {
    const dates: Record<string, string | undefined> = { expiry_date, given_date, scheduled_date };
    Object.entries(dates).forEach(([key, val]) => {
      if (
        val &&
        ValidatorUtils.isInt(val) &&
        parseInt(val) > 9999 &&
        !ValidatorUtils.stringMatchesDate(val, DATE_FORMAT)
      ) {
        setValue(
          key as keyof AdministeredDrugValues,
          CalendarUtils.format(parseInt(val), DATE_FORMAT),
        );
      }
    });
  }, [expiry_date, given_date, scheduled_date, setValue]);

  const getPhysicianNames = () => {
    let filteredPhysicians = StaffUtils.filterActiveUsers(physicians);

    let ownName: string | null = null;
    let names: string[] = filteredPhysicians.map((physician: any) => {
      let physicianName: string = PhysicianUtils.getName(physician) || '';
      if (physician.id === authentication.user?.id) ownName = physicianName;
      return physicianName;
    });

    // NEW records only. Editing repopulates the form from the record itself,
    // and a historical record saved with no Given By must stay unattributed —
    // stamping whoever opened it would falsify who administered the dose.
    const currentGivenBy = getValues('given_by');
    if (ownName && !currentGivenBy && !itemId) {
      setValue('given_by', ownName);
    }
    setPhysicianNames(names);
  };

  const clearForm = () => {
    setVitalitySelection('');
    reset();
  };

  const isNew = !itemId;

  const buttonDisabled = !nameValue || (!given_date && !scheduled_date) || removing;

  // The selected tenant drug entry's fee options. Resolved by entry id when
  // one was picked, falling back to name so it is correct on edit too.
  const selectedDrug =
    (feeEntryId ? drugDescriptions.find((d) => d.id === feeEntryId) : undefined) ||
    drugDescriptions.find((d) => d.name === nameValue);
  const fees: any[] = selectedDrug?.fees ?? [];
  const selectedFee = fees.find((fee: any) => fee.id === feeId);

  const onPickVitalityDrug = (drugName: string) => {
    if (!drugName) return;
    const drug = drugDescriptions.find((d) => d.name === drugName);
    if (!drug) return;

    setValue('name', drug.name ?? '');
    setValue('description', drug.description ?? '');
    setValue('route', drug.route ?? '');
    setValue('feeEntryId', drug.id ?? '');
    setValue('feeId', '');
    setValue('paid', false);
    if (!getValues('given_date') && !getValues('scheduled_date')) {
      setValue('given_date', CalendarUtils.format(moment().unix(), DATE_FORMAT));
    }
    setVitalitySelection(drugName);
  };

  async function addAdministeredDrug(values: AdministeredDrugValues) {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      let value: any = PerformanceUtils.clone(values);
      let id = value.itemId;
      delete value.itemId;
      delete value[form];

      if (value.expiry_date) {
        value.expiry_date = CalendarUtils.convertToTargetTimezone(
          moment(value.expiry_date, DATE_FORMAT),
        ).unix();
      } else {
        delete value.expiry_date;
      }

      if (value.given_date) {
        value.given_date = CalendarUtils.convertToTargetTimezone(
          moment(value.given_date, DATE_FORMAT),
        ).unix();
      } else {
        delete value.given_date;
      }

      if (value.scheduled_date) {
        value.scheduled_date = CalendarUtils.convertToTargetTimezone(
          moment(value.scheduled_date, DATE_FORMAT),
        ).unix();
        if (!value.given_date) delete value.given_by;
      } else {
        delete value.scheduled_date;
      }

      let {
        followUp: followUpVal,
        followUpDate,
        feeEntryId: entryIdVal,
        feeId: feeIdVal,
        paid: paidVal,
      } = value;
      delete value.followUp;
      delete value.followUpDate;
      delete value.feeEntryId;
      delete value.feeId;
      delete value.paid;

      let followUpDrug =
        !!followUpVal && !!followUpDate
          ? {
              patient_id: patient.id,
              value: {
                ...value,
                given_date: undefined,
                expiry_date: undefined,
                lot: undefined,
                given_by: undefined,
                notes: undefined,
                scheduled_date: followUpDate,
              },
            }
          : undefined;

      let drug = {
        id,
        patient_id: patient.id,
        value: { ...value, paid: paidVal },
      };

      setLoading(true);
      // The server resolves the amount from the fee reference — the client
      // never sends a raw amount.
      const chargeParams =
        feeIdVal && !paidVal ? { feeEntryId: entryIdVal || selectedDrug?.id, feeId: feeIdVal } : {};

      let result = await dispatch(
        api.HealthRecords.addAdministeredDrug({ administeredDrug: drug, ...chargeParams }),
        {
          title: 'Administered Drug',
          successText: 'Administered Drug added successfully!',
        },
      );
      setLoading(false);
      if (result) {
        if (followUpDrug) {
          await dispatch(
            api.HealthRecords.addAdministeredDrug({ administeredDrug: followUpDrug }),
            { errorTitle: 'Follow-up Administered Drug' },
          );
        }
        setValue('followUp', false);
        setVitalitySelection('');
        reset();
      }
    } finally {
      inFlightRef.current = false;
    }
  }

  async function removeAdministeredDrug() {
    setRemoving(true);
    let result = await dispatch(
      api.HealthRecords.removeAdministeredDrug({
        id: itemId,
        patientId: patient.id,
      }),
      {
        title: 'Remove Administered Drug',
        successText: 'Administered Drug removed successfully!',
      },
    );
    setRemoving(false);
    if (result) {
      reset();
    }
  }

  return (
    <>
      {onClose !== undefined && (
        <TopBar title="Administered Drug" onClose={onClose} onClear={clearForm} />
      )}
      <div className="details-panel new-administered-drug">
        <div className="row form">
          <DefaultSelectField
            label="Prefilled Drugs"
            value={vitalitySelection}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onPickVitalityDrug(event.target.value)
            }
            inputs={drugDescriptions.map((d) => d.name ?? '')}
            style={{ marginRight: '20px', width: '200px' }}
          />
          <DefaultTextField
            name="name"
            control={control}
            label="Name"
            disabled={loading}
            style={{ width: '250px', marginRight: '20px' }}
          />
          <DefaultTextField
            name="description"
            control={control}
            label="Description"
            disabled={loading}
            style={{ width: '250px', marginRight: '20px' }}
          />
          <DefaultAutoComplete
            name="route"
            control={control}
            placeholder="Route"
            dataSource={autoCompleteSource.route}
            allowClear={true}
          />
          <DefaultAutoComplete
            name="site"
            control={control}
            placeholder="Site"
            dataSource={autoCompleteSource.site}
            allowClear={true}
          />
          <DefaultTextField
            name="lot"
            control={control}
            label="Lot #"
            disabled={loading}
            style={{ width: '150px', marginRight: '20px' }}
          />
          <DefaultTextField
            name="expiry_date"
            control={control}
            label={'Expiry ' + DATE_FORMAT}
            style={{ width: '170px', marginRight: '10px' }}
          />
          <DefaultTextField
            name="given_date"
            control={control}
            label={'Given ' + DATE_FORMAT}
            style={{ width: '170px', marginRight: '10px' }}
          />
          <div className="given-button-wrapper">
            <DefaultButton
              name="Given Today"
              onClick={() => {
                setValue('given_date', CalendarUtils.format(moment().unix(), 'YYYY/MM/DD'));
              }}
              style={{ width: '130px' }}
            />
          </div>
          <DefaultTextField
            name="dosage"
            control={control}
            label="Dosage"
            disabled={loading}
            style={{ width: '150px', marginRight: '20px' }}
          />
          <DefaultSelectField
            name="given_by"
            control={control}
            label="Given By"
            inputs={['External', ...physicianNames]}
            style={{ width: '200px', marginRight: '20px', display: 'inline-block' }}
          />
          <DefaultTextField
            name="notes"
            control={control}
            label="Notes"
            disabled={loading}
            style={{ width: '320px', marginRight: '20px' }}
          />
          {!isNew && (
            <DefaultTextField
              name="scheduled_date"
              control={control}
              label={'Scheduled ' + DATE_FORMAT}
              style={{ width: '190px', marginRight: '10px' }}
            />
          )}
        </div>
        <div className="follow-up">
          {paid && <span className="mr-3">This drug has been successfully paid</span>}
          {fees.length === 1 && !paid && StripeUtils.isEnabled() && (
            <Controller
              name="feeId"
              control={control}
              render={({ field }) => (
                <div
                  style={{
                    display: 'inline-block',
                    color: 'rgb(131, 131, 149)',
                    marginTop: '30px',
                    marginRight: '15px',
                  }}
                >
                  <input
                    type="checkbox"
                    id="feeId"
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked ? fees[0].id : '')}
                  />
                  <span>{` Charge ${fees[0].label} — $${fees[0].amount}`}</span>
                </div>
              )}
            />
          )}
          {fees.length > 1 && !paid && StripeUtils.isEnabled() && (
            <Controller
              name="feeId"
              control={control}
              render={({ field }) => (
                <DefaultSelectField
                  value={field.value ?? ''}
                  label="Charge"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    field.onChange(event.target.value)
                  }
                  inputElements={[
                    <MenuItem value="" key="no-charge">
                      No charge
                    </MenuItem>,
                    ...fees.map((fee: any) => (
                      <MenuItem value={fee.id} key={fee.id}>
                        {`${fee.label} — $${fee.amount}`}
                      </MenuItem>
                    )),
                  ]}
                  style={{ marginRight: '20px', width: '200px' }}
                />
              )}
            />
          )}
          <RHFCheckbox name="followUp" control={control} label="Schedule next dose?" />
          {!!followUp && (
            <DefaultDatePicker
              name="followUpDate"
              control={control}
              label="Next Dose Date"
              style={{ width: '145px', marginLeft: '15px', display: 'inline-block' }}
            />
          )}
        </div>
        <div className="button-wrapper">
          <DefaultButton
            name="Clear"
            onClick={clearForm}
            style={{ marginRight: '20px', width: '150px' }}
          />
          <DefaultButton
            name={selectedFee && !paid ? `Save & Charge $${selectedFee.amount}` : 'Save'}
            onClick={handleSubmit(addAdministeredDrug)}
            loading={loading}
            className="blue"
            style={selectedFee && !paid ? { width: '200px' } : undefined}
            disabled={buttonDisabled}
          />
          {!isNew && (
            <DefaultButton
              name="Remove"
              onClick={removeAdministeredDrug}
              className="red"
              disabled={loading}
              loading={removing}
              style={{ marginLeft: '20px', width: '150px' }}
            />
          )}
        </div>
      </div>
    </>
  );
};

const RHFCheckbox = ({
  name,
  control,
  id,
  label = 'Transcribed',
  style = {},
}: {
  name: string;
  control: any;
  id?: string;
  label?: string;
  style?: React.CSSProperties;
}) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <div
        style={{
          display: 'inline-block',
          borderBottom: fieldState.isTouched && fieldState.error ? '2px solid red' : 'none',
          color: 'rgb(131, 131, 149)',
          marginTop: '30px',
          ...style,
        }}
      >
        <input
          type="checkbox"
          id={id || name}
          name={name}
          checked={field.value === true}
          onChange={(e) => field.onChange(e.target.checked)}
          onBlur={field.onBlur}
          ref={field.ref}
        />
        <span> {label}</span>
      </div>
    )}
  />
);

export default NewAdministeredDrug;

const autoCompleteSource: Record<string, string[]> = {
  route: [
    'Inhaled',
    'Intramuscular (IM)',
    'Intradermal (ID)',
    'Intranasal',
    'Intrauterine',
    'Intravenous (IV)',
    'Oral (PO)',
    'Topical',
    'Subcutaneous (SC)',
  ],
  site: [
    'Abdomen',
    'Left deltoid',
    'Right deltoid',
    'Left arm',
    'Right arm',
    'Left thigh',
    'Right thigh',
    'Left forearm',
    'Right forearm',
  ],
};
